"use client";

import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useRouter, usePathname } from "next/navigation";

import { createDocument, disposeApi } from "@/api";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { useStore } from "@/store";
import { docPageUrl } from "@/urls";
import { getFirstDocumentOfTeam } from "@/utils/getFirstDocumentOfTeam";
import { useLastDocId } from "@/utils/useLastDocId";

const CreateDocOrRedirectImpl = observer(function CreateDocOrRedirectImpl({
  children,
}: {
  children?: React.ReactNode;
}) {
  const router = useRouter();
  const { lastDocId } = useLastDocId();
  const pathname = usePathname();
  const { documentTree } = useStore();
  const { team } = documentTree;

  useEffect(() => {
    if (lastDocId) {
      const url = docPageUrl({ docId: lastDocId });
      if (url !== pathname) {
        router.replace(url);
      }

      return undefined;
    }

    if (!team) {
      return undefined;
    }

    const firstDoc = getFirstDocumentOfTeam(team);
    if (firstDoc) {
      router.replace(docPageUrl({ docId: firstDoc.uid }));
      return undefined;
    }

    return disposeApi(async (signal) => {
      const { document: doc } = await createDocument({}, { signal });

      if (doc) {
        const { uid } = doc;
        router.replace(docPageUrl({ docId: uid }));
      }
    });
  }, [router, lastDocId, pathname, team]);

  return children ?? null;
});

export const CreateDocOrRedirect = observer(function CreateDocOrRedirect({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <RequireAuth noRedirectBack>
      <CreateDocOrRedirectImpl>{children}</CreateDocOrRedirectImpl>
    </RequireAuth>
  );
});
